import {Component, Inject, OnInit} from '@angular/core';
import {MatDialog, MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {OrganizationService} from "../../../../../service-data/organization.service";
import {Organization} from "../../../../../../model/Organization";
import {ProjectFolders} from "../../../../../../model/supporting/ProjectFolders";
import {Util} from "../../../../../util/Util";
import {Project} from "../../../../../../model/Project";
import {ProjectService} from "../../../../../service-data/project.service";
import {LanguagePipe} from "../../../../../pipe/language.pipe";

@Component({
  selector: 'app-move-project',
  templateUrl: './move-project.component.html',
  styleUrls: ['./move-project.component.css']
})
export class MoveProjectComponent implements OnInit {
  public breakpoint: number; // Breakpoint observer code
  public changeForm: FormGroup;
  /**
   * Every Project this dialog is moving.
   *
   * One Project is the length-1 case, not a separate mode -- the tile
   * menus pass `project` and the grid selection passes `projects`, and
   * the constructor collapses both into this. Two move implementations
   * that drift apart is the failure this avoids; the only thing that
   * varies below is wording.
   */
  projects: Project[] = [];

  /** First of `projects`. Titles and the tree's initial highlight. */
  project: Project;

  /** How many actually moved, for the close payload. */
  movedCount = 0;

  /** Names (or server wording) of the Projects that did not move. */
  failures: string[] = [];

  wasFormChanged = false;
  organization: Organization;
  changeValid = false;
  currentFolder: string;
  projectFolders: ProjectFolders;
  newFolder = '';
  protected readonly Util = Util;

  /** True while the move request is in flight — blocks double submits. */
  moving = false;

  /** Failure text, shown inline so the dialog can stay open. */
  errorMessage = '';

  constructor(private formBuilder: FormBuilder, public dialog: MatDialog, public projectService: ProjectService, @Inject(MAT_DIALOG_DATA) data, private dialogRef: MatDialogRef<MoveProjectComponent>) {
    // Get data passed in
    if ( data.organization ) {
      this.organization = data.organization;
      this.projectFolders = this.organization.getProjectFolders();
      // `projects` (a selection made in the grid) or `project` (one
      // tile's own overflow menu). Both callers exist and neither is
      // going away, so they are collapsed here rather than at three
      // call sites.
      //
      // COPIED, not aliased. The array handed in comes from a getter
      // over the grid's live selection, which change detection
      // recomputes and which the operator can still alter behind a
      // non-modal overlay; the dialog must move the set that was on
      // screen when the button was pressed.
      if ( data.projects && data.projects.length ) {
        this.projects = data.projects.slice();
      } else if ( data.project ) {
        this.projects = [data.project];
      }

      this.project = this.projects.length ? this.projects[0] : undefined;
      // Seeds the tree's highlight only. Each Project's own raw folder
      // is what gets sent -- see `moveNext`.
      this.currentFolder = this.project ? this.project.folder : '';
      this.newFolder = this.currentFolder;
    }
  }

  /** More than one Project: switches the wording, not the behaviour. */
  get isBulk(): boolean {
    return this.projects.length > 1;
  }

  public ngOnInit(): void {
    this.changeForm = this.formBuilder.group({
      newFolder: [this.newFolder]
    });

    this.breakpoint = window.innerWidth <= 600 ? 1 : 2; // Breakpoint observer code
  }

  /**
   * Move the Project to `newFolder`.
   *
   * ---------------------------------------------------------------------
   * Why this is neither `projectService.updateItem(project)` nor
   * `moveFolders(...)`
   * ---------------------------------------------------------------------
   * It used to set `project.folder` and PUT the whole Project. On
   * 2026-09-02 the server narrowed `PUT /project/{id}` to an allow-list
   * (`Projects.SAVE_ALLOWED_FIELDS` -- name, archived, primaryBook_ID,
   * primaryDraft_ID) and merges only those onto the stored row. `folder`
   * is not on it, so the generic PUT **silently ignored the move**: 200,
   * with the unchanged stored row echoed back.
   *
   * That narrowing is one we asked for -- two other call sites change only
   * `name` and were republishing a stale `folder` as a side effect, which
   * silently reverted concurrent moves.
   *
   * It then briefly used `POST /project/movefolders/{id}/{from}/{to}`,
   * which writes `project.folder` directly and so escapes the allow-list.
   * That route cannot express the ROOT: the arguments are base64 in the
   * URL, `Util.toBase64('')` is '', and an empty segment matched no route.
   * Moving a Project into or out of My Projects therefore had to be
   * refused in the UI -- which is most of the moves an operator makes.
   *
   * `PUT /project/{id}/folder` (shipped 2026-09-02) carries the value in
   * the BODY as a plain string, so '' is expressible and the root works in
   * either position. The guard that used to sit here is gone with it.
   *
   * `currentFolder` is the RAW `project.folder` captured when the dialog
   * opened, deliberately un-normalized: the server compares it byte-for-
   * byte, so normalizing a stored '' into '/' would fail the optimistic-
   * concurrency check for every Project at the root. On refusal the server
   * hands back the value it actually holds in `data.folder`, so a stale
   * dialog re-arms against reality instead of making the operator reopen
   * it.
   */
  /**
   * Move every Project in `projects` to `newFolder`.
   *
   * ---------------------------------------------------------------------
   * Why a set of Projects is a LOOP and not one batch call
   * ---------------------------------------------------------------------
   * There is no batch form, and that is not an oversight to be worked
   * around. `PUT /project/{id}/folder` takes one id in the path and
   * carries a per-Project optimistic-concurrency check in the body, and
   * that check is most of the reason the endpoint exists -- a batch route
   * that could not refuse individual members would be a different and
   * weaker endpoint, not a convenience wrapper over this one.
   *
   * So a multiple move is N independent writes with N independent
   * refusals, and a genuinely PARTIAL outcome is a normal result rather
   * than an edge case. It is reported as one: the dialog stays open and
   * names what did not move. Closing on `3 of 5 moved` would leave the
   * operator believing a set is together when it is still split across
   * two folders, with nothing on screen to say which two -- and the grid
   * behind the dialog would look consistent either way.
   */
  public onChange(): void {
    if ( this.moving || !this.projects.length ) {
      return;
    }
    this.errorMessage = '';

    // Normalized on write. The tree emits '/' for the root while
    // `Project.folder` spells it '', and `ProjectsComponent` compares
    // with `isProjectInFolder` (which normalizes) -- but the raw grid
    // filter and the folder counts both key off this string, so
    // storing '/' here would put the Project in a folder that reads
    // as neither the root nor anything else.
    const destination = Util.normalizeFolderPath(this.newFolder);

    // Projects already in the destination are not sent. The endpoint
    // would accept the no-op, but each one is a round trip and a
    // concurrency check that can fail for no gain. Gathering a spread
    // selection into one folder normally means some members are already
    // there.
    const pending = this.projects.filter(p => p && Util.normalizeFolderPath(p.folder) !== destination);
    if ( pending.length === 0 ) {
      this.dialogRef.close({ moved: false, folder: destination });
      return;
    }

    this.moving = true;
    this.movedCount = 0;
    this.failures = [];
    this.moveNext(pending, 0, destination);
  }

  /**
   * Issue one PUT, then recurse. Sequential, not forkJoin.
   *
   * These are independent writes with independent refusals, so there is
   * nothing to gain from racing them and two things to lose: a fan-out of
   * fifty concurrent writes against one account is a burst this endpoint
   * has never been asked to absorb, and the failure list would come back
   * in completion order rather than in the order the operator sees the
   * tiles.
   *
   * `project.folder` is read HERE rather than captured when the dialog
   * opened, and raw. The server compares it byte-for-byte, so
   * normalizing a stored '' into '/' would fail the concurrency check for
   * every Project sitting at the root -- which is the case this endpoint
   * was introduced to serve.
   */
  private moveNext(pending: Project[], index: number, destination: string): void {
    if ( index >= pending.length ) {
      this.finishMove(destination);
      return;
    }

    const project = pending[index];
    const from = project.folder;

    this.projectService.setProjectFolder(project.getId(), destination, from).subscribe(
      response => {
        // The endpoint reports its own refusals in a ResponseMessage
        // envelope carried on a 200 -- a stale `currentFolder` is not an
        // HTTP error -- so the envelope has to be checked even though the
        // error branch below really does fire for transport failures.
        const status = response ? response.status : undefined;
        if ( status === 'SUCCESS' ) {
          // Only now is the in-memory Project safe to update. Assigning
          // before the call meant a failed move still moved the Project
          // on screen until the next reload.
          project.folder = destination;
          this.movedCount++;
          console.log(`Moved Project ${project.getId()} from ${from} to ${destination}`);
        } else {
          // Concurrency refusal: `data.folder` is where the Project
          // actually is. Take it -- the grid is currently drawing this
          // Project somewhere it is not.
          const actual = response && response.data ? response.data.folder : undefined;
          if ( actual !== undefined && actual !== null ) {
            project.folder = actual;
          }
          this.failures.push(this.labelFor(project, response));
          console.error(`Move Project ${project.getId()} from [${from}] to [${destination}] did not succeed`, response);
        }
        this.moveNext(pending, index + 1, destination);
      },
      err => {
        // Carry on down the list. One transport failure says nothing
        // about the next Project, and stopping here would produce a
        // partial move that is partial for no reason the operator can
        // see.
        this.failures.push(this.labelFor(project, undefined));
        console.error(`Error moving Project ${project.getId()} from [${from}] to [${destination}]`, err);
        this.moveNext(pending, index + 1, destination);
      }
    );
  }

  /** How a failed Project is named back to the operator. */
  private labelFor(project: Project, response: any): string {
    const name = project && project.name ? project.name : String(project ? project.getId() : '');
    // The server's own wording, when it gave one, is more specific than
    // anything this dialog can say -- but only for a single Project, where
    // there is room to show it in full.
    if ( this.projects.length === 1 && response && response.message ) {
      return response.message;
    }
    return name;
  }

  /**
   * Close on a clean sweep; stay open and explain otherwise.
   *
   * `changeValid` is recomputed against where the Projects ACTUALLY are
   * now, so pressing the button a second time is a real attempt rather
   * than a replay of the same stale comparison.
   */
  private finishMove(destination: string): void {
    this.moving = false;
    this.currentFolder = this.project ? this.project.folder : destination;

    if ( this.failures.length === 0 ) {
      this.newFolder = destination;
      this.markAsDirty(this.changeForm);
      this.dialogRef.close({ moved: true, folder: destination, count: this.movedCount });
      return;
    }

    this.errorMessage = this.projects.length === 1
      ? ( this.failures[0] || LanguagePipe.get('The Project could not be moved.') )
      : LanguagePipe.get('Some Projects could not be moved.') + ' ' + this.failures.join(', ');
    this.changeValid = this.projects.some(p => p && Util.normalizeFolderPath(p.folder) !== destination);
  }

  // tslint:disable-next-line:no-any
  public onResize(event: any): void {
    this.breakpoint = event.target.innerWidth <= 600 ? 1 : 2;
  }

  private markAsDirty(group: FormGroup): void {
    group.markAsDirty();
    // tslint:disable-next-line:forin
    for (const i in group.controls) {
      group.controls[i].markAsDirty();
    }
  }

  formChanged() {
    this.wasFormChanged = true;
  }

  handleSelection(name: string) {
    this.newFolder = name;
    // Normalized comparison. The root is spelled '' on `Project.folder`
    // (the model default) and '/' by the tree, and a raw !== between
    // those says a Project already at the root is being moved
    // somewhere new.
    //
    // `some`, not `every`: for a multiple selection the destination only
    // has to be new for ONE member. Gathering Projects that are spread
    // across folders into a single folder is the ordinary use, and
    // requiring every member to be moving would disable the button as
    // soon as any one of them was already there.
    const destination = Util.normalizeFolderPath(name);
    this.changeValid = this.projects.some(p => p && Util.normalizeFolderPath(p.folder) !== destination);
    console.log('User selected:', name);
  }

  /**
   * Destination as the operator would name it, for the button label.
   * The raw path is not it: the button used to read "Move to /" at the
   * root, and "Move to /Fiction/2026" elsewhere.
   */
  get newFolderLabel(): string {
    if ( !this.newFolder || Util.normalizeFolderPath(this.newFolder) === '' ) {
      return LanguagePipe.get('My Projects');
    }
    return Util.getLastItem(this.newFolder, '/');
  }
}



